import type { Mailbox } from '@/services/mailboxes'
import { isGroupSource } from './groupSourceText'

/**
 * SA-1386: the one line a mailbox row shows while its connection is in
 * trouble. Pure. Times are the browser's local time, hand-formatted.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const two = (n: number) => String(n).padStart(2, '0')

/** The server's datetimes are naive UTC with no offset: read them as UTC. */
function parseUtc(iso: string): Date {
  return new Date(/(?:Z|[+-]\d\d:?\d\d)$/i.test(iso) ? iso : `${iso}Z`)
}

function when(iso: string, now: Date): string {
  const d = parseUtc(iso)
  const time = `${two(d.getHours())}:${two(d.getMinutes())}`
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  return sameDay ? time : `${d.getDate()} ${MONTHS[d.getMonth()]} ${time}`
}

export function healthLine(
  mb: Pick<Mailbox, 'status' | 'channel' | 'failing_since' | 'next_retry_at' | 'retry_count'>,
  now: Date,
): string | null {
  if (mb.status === 'paused' || !mb.failing_since) return null
  if (mb.status === 'active') {
    const base = `Failing since ${when(mb.failing_since, now)}`
    return mb.next_retry_at ? `${base} · next retry ${when(mb.next_retry_at, now)}` : base
  }
  if (mb.status === 'reauth_required' && !isGroupSource(mb)) {
    const retry = (mb.retry_count ?? 0) + 1
    return mb.next_retry_at
      ? `Reconnecting on its own · retry ${retry} at ${when(mb.next_retry_at, now)}`
      : 'Reconnecting on its own'
  }
  return null
}
